import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements';

import { currencyFormat } from '@libs/core';

import type { FilterType, FilterValueType, ItemFilterType } from '../types';
import { FILTERS } from './filters';

const isPriceRange = (attribute: string) => attribute === FILTERS.RANGE.ATTRIBUTE;

const isRating = (attribute: string) => attribute === FILTERS.RATING.ATTRIBUTE;

const isAutoship = (attribute: string) => attribute === FILTERS.SUBSCRIPTION_ENABLED.ATTRIBUTE;

const isSDD = (attribute: string) => attribute === FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ATTRIBUTE;

export const isEligibleValue = (value: unknown) => [true, 'true'].includes(value as boolean | string);

export const getRatingValues = (values: FilterValueType[]) => {
    const [item] = values;
    return `${item.value} ${
        item.value === 1 ? FILTERS.RATING.STAR : FILTERS.RATING.STARS
    }${item.value < 5 ? FILTERS.RATING.UP : ''}`;
};

const getLabelFilter = ({ attribute, refinements }: CurrentRefinementsConnectorParamsItem) => {
    if (isRating(attribute)) {
        return getRatingValues(refinements as FilterValueType[]);
    }
    return `(${refinements.length})`;
};

export const countFilters = (items: CurrentRefinementsConnectorParamsItem[]) => {
    const counters = items.reduce((filters, current) => {
        filters[current.attribute] = getLabelFilter(current);
        return filters;
    }, {} as FilterType);
    return (attibute: string, defaultTitle = '') => counters[attibute] ?? defaultTitle;
};

export const totalFilters = (items: CurrentRefinementsConnectorParamsItem[]) =>
    items.reduce((value, current) => value + (isPriceRange(current.attribute) ? 1 : current.refinements.length), 0);

export const transformFilters = (items: CurrentRefinementsConnectorParamsItem[]) =>
    items
        .filter(item => FILTERS.RANGE.ATTRIBUTE !== item.attribute)
        .flatMap(item => {
            if (FILTERS.RATING.ATTRIBUTE === item.attribute) {
                return item.attribute;
            }
            if (FILTERS.SHOP_BY_STORE.ATTRIBUTE === item.attribute) {
                return item.attribute;
            }
            if (isAutoship(item.attribute) || isSDD(item.attribute)) {
                return item.refinements.some(ref => isEligibleValue(ref.value)) ? item.attribute : [];
            }
            return item.refinements.map(ref => `${item.attribute}|${ref.value}`);
        });

export const getRangeTitle = (minValue: number, maxValue: number, space = false) => {
    const ranges = [currencyFormat(minValue), currencyFormat(maxValue)];
    if (minValue === FILTERS.RANGE.MIN_VALUE && maxValue === FILTERS.RANGE.MAX_VALUE) {
        return FILTERS.RANGE.TITLE;
    }
    return `${ranges.join(space ? ' - ' : '-')}${maxValue >= FILTERS.RANGE.MAX_VALUE ? '+' : ''}`;
};

export const transformFilterItems = (items: CurrentRefinementsConnectorParamsItem[]) => {
    const results: ItemFilterType = {};
    items
        .filter(item => FILTERS.RANGE.ATTRIBUTE !== item.attribute)
        .forEach(item => {
            if (FILTERS.RATING.ATTRIBUTE === item.attribute) {
                results[item.attribute] = {
                    attribute: item.attribute,
                    refine: item.refine,
                    label: getRatingValues(item.refinements as FilterValueType[]),
                    refinement: item.refinements[0],
                };
            } else if (FILTERS.SHOP_BY_STORE.ATTRIBUTE === item.attribute) {
                results[item.attribute] = {
                    attribute: item.attribute,
                    refine: item.refine,
                    label: FILTERS.SHOP_BY_STORE.LABEL,
                    refinement: item.refinements[0],
                };
            } else if (isAutoship(item.attribute)) {
                const autoshipRefinement = item.refinements.find(ref => isEligibleValue(ref.value));

                if (autoshipRefinement) {
                    results[item.attribute] = {
                        attribute: item.attribute,
                        refine: item.refine,
                        label: FILTERS.SUBSCRIPTION_ENABLED.ELIGIBLE_LABEL,
                        refinement: autoshipRefinement,
                    };
                }
            } else if (isSDD(item.attribute)) {
                const sddRefinement = item.refinements.find(ref => isEligibleValue(ref.value));

                if (sddRefinement) {
                    results[item.attribute] = {
                        attribute: item.attribute,
                        refine: item.refine,
                        label: FILTERS.SCHEDULED_DELIVERY_ELIGIBLE.ELIGIBLE_LABEL,
                        refinement: sddRefinement,
                    };
                }
            } else {
                item.refinements.forEach(ref => {
                    results[`${item.attribute}|${ref.value}`] = {
                        attribute: item.attribute,
                        label: ref.label,
                        value: ref.value,
                        refine: item.refine,
                        refinement: ref,
                    };
                });
            }
        });

    return results;
};

export const getNumerFromstring = (stringNumber: string) =>
    Number.parseInt(stringNumber?.replace(/\D/g, ''), 10) || undefined;
